import React, { useMemo, useState } from 'react';
import s from './StyledHTMLFieldsBlock.module.scss';
import { useSelector } from 'react-redux';
import { Editor } from '@tinymce/tinymce-react';
import { clearHTML } from 'utils/clearHTML';
import { useStatusContext } from 'components/StatusProvider';
import { ReactComponent as LoadingIcon } from "assets/icons/loading.svg";
import translateIcon from "assets/icons/translate.webp";
import { translateText } from 'utils/translateText';
import { DropdownBlock } from 'components/DropdownBlock/DropdownBlock';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@mui/material';


type StyledHTMLFieldsBlockType = {
    title: string,
    values: {[key: string]: string},
    changeValue: (value: {[key: string]: string}) => void
    setLang?: (str:string) => void
    activeLang: string
    setActiveLang?: any
    style?: React.CSSProperties
    isClear?: boolean
    height?: number
    setActive?: any
    active?: boolean
}

export const StyledHTMLFieldsBlock: React.FC<StyledHTMLFieldsBlockType> = ({title, values, changeValue, setLang, activeLang, setActiveLang, style, isClear, height, active = true, setActive}) => {

    const langs = useSelector((store: any) => store.main.langs);
    const [isLoadingTranslate, setIsLoadingTranslate] = useState<boolean>(false);
    const { openStatus } = useStatusContext();
    const MAX_TRANSLATE_LENGTH = 5000;
    const {t} = useTranslation()
    
    if(!langs) return <></>
    const langs_view = langs?.map((el: any, index: number) => {
        const completed = values?.[el.key]?.length
        return(
            <div 
                key={index}
                className={`${s.lang_item} ${activeLang === el.key? s.active_lang_item : ''} ${completed? s.completed_lang_item : ''}`}
                onClick={()=>{
                    setActiveLang(el.key)
                    if(setLang) setLang(el.key)
                }}
            >
                {el.key}
                <span>{completed? <span>&#10004;</span> : <span>&#10008;</span>}</span>
            </div>
        )
    })

    function getCurrentValue(){
            let value
            
            for(let i=0; i<langs?.length; i++){
                if(values[langs?.[i].key]?.length){
                    value = values[langs?.[i].key]
                    break
                }
            }
            
            return value
        }
    
        const handleTranslate = async () => {
            try {
                setIsLoadingTranslate(true);
                const text = getCurrentValue();
    
                if (!text) {
                    setIsLoadingTranslate(false);
                    openStatus('error', "status.notPicture");
                    return;
                }
    
                const chunks = [];
                let chunkStart = 0;
    
                while (chunkStart < text.length) {
                    let chunkEnd = chunkStart + MAX_TRANSLATE_LENGTH;
                    if (chunkEnd < text.length) {
                        const lastSpaceIndex = text.lastIndexOf(' ', chunkEnd);
                        if (lastSpaceIndex > chunkStart) {
                            chunkEnd = lastSpaceIndex;
                        }
                    }
                    chunks.push(text.slice(chunkStart, chunkEnd));
                    chunkStart = chunkEnd + 1;
                }
    
                const languages = langs?.map((l: any) => l.key) || [];
                const results = await Promise.all(chunks.map(chunk => translateText(chunk, languages)));
    
                const mergedResult = results.reduce((acc, part) => {
                    for (const key in part) {
                        acc[key] = (acc[key] || '') + part[key];
                    }
                    return acc;
                }, {} as Record<string, string>);
                
                Object.entries(mergedResult).forEach(obj => {
                    changeValue({[obj[0]]: obj[1]})
                })
                
                setIsLoadingTranslate(false);
            } catch (error) {
                setIsLoadingTranslate(false);
                openStatus('error', "status.translationError");
                console.error("Error during translation:", error);
            }
        };

    return(
        <div className={s.wrapper} style={style}>
            <header>
                <p style={{opacity: setActive && !active ? 0 : 1}}>{title}</p>
                <div className={s.langs_wrapper} style={{opacity: setActive && !active ? 0 : 1}}>
                    {langs_view}
                </div>
                <div className={s.translator_wrapper} style={{opacity: setActive && !active ? 0 : 1}}>
                    {isLoadingTranslate ? 
                        <LoadingIcon className={s.loadingIcon} /> :
                        <img
                            src={translateIcon}
                            alt={"translateIcon"}
                            className={s.translateIcon}
                            onClick={handleTranslate}
                        />
                    }
                </div>
                {
                    setActive ? 
                    <div className={s.active_wrapper}>
                        <span className={s.active_title}>{`${t('blog.use_alternative')} ${title}`}</span>
                        <Checkbox
                            checked={active}
                            onChange={(_, value)=>setActive(value)}
                        />
                    </div> : null
                }
            </header>
            <DropdownBlock open={active}>
                <div className={s.input_wrapper}>
                    <Editor
                        tinymceScriptSrc='/tinymce/tinymce.min.js'
                        init={{
                            branding: false,
                            width: 'inherit',
                            skin: "oxide-dark",
                            content_css: "dark",
                            height: height || 500,
                            menubar: false,
                            plugins: "image link media lists",
                            toolbar:
                                "styleselect undo redo | h1 h2 h3 | bold italic underline strikethrough | image link media alignleft aligncenter alignright alignjustify numlist bullist | bullist outdent indent | removeformat ",
                            image_advtab: false,
                            image_class_list: [
                                {title: 'Center', value: 'image_center'},
                                {title: 'Left', value: 'image_left'},
                                {title: 'Right', value: 'image_right'},
                                {title: 'Left Float', value: 'image_left_float'},
                                {title: 'Right Float', value: 'image_right_float'}
                            ],
                            style_formats: [
                                {
                                    title: 'image_left_float', selector: 'image_left_float', styles: {
                                        'float': 'left',
                                        'margin': '0 10px 0 10px'
                                    }
                                }
                            ],
                            content_style: `
                        body {
                            background-color: #232428;
                        }
                    `,
                        }}
                        value={values?.[activeLang]?.length? values[activeLang] : ''}
                        onEditorChange={(value)=>{
                            if(value || value === ""){
                                // console.log("value", value);
                                changeValue({[activeLang]: isClear ? clearHTML(value) : value})
                            }
                        }}
                    />
                </div>
            </DropdownBlock>
        </div>
    )
}